<template>
  <div class="product-safety-content nutrition-info">
    <div class="title-area">
        <div class="title">Ürün Besin Bilgileri ve Değerleri</div>
        <div class="title-sub">Ürün Besin Bilgileri</div>
        <div class="description">Ürün besin bilgileri, gıda ürünlerine dair özellik ve uyarıları içerir.</div>
    </div>
    <div class="product-safety-content-boxes">
        <div class="box">
          <Input 
            type="textarea" 
            label="Alerjen Uyarısı" 
            id="allergen-warning"
            v-model:value="nutritionData.allergenWarning"
          />
          <Input 
            type="textarea" 
            label="Kullanım Önerileri" 
            id="usage-recommendations"
            v-model:value="nutritionData.usageRecommendations"
          />
          <DatePickerV2
            title="Son Tüketim Tarihi"
            label="Son Tüketim Tarihi" 
            class="last-consumption-date"
            :value="expirationDateTimestamp"
            :min-date="minDate"
            :max-date="maxDate"
            @selectedValue="(value) => nutritionData.expirationDate = formatDate(value)"
          />
        </div>
        <div class="box">
          <Input 
            type="textarea" 
            label="Muhafaza Koşulları" 
            id="storage-conditions"
            v-model:value="nutritionData.storageConditions"
          />
          <Input 
            type="textarea" 
            label="İçindekiler" 
            id="contents" 
            :maxLength="4000"
            v-model:value="nutritionData.contents"
          />
          <DatePickerV2 
            title="Tavsiye Edilen Tüketim Tarihi"
            label="Tavsiye Edilen Tüketim Tarihi" 
            class="recommended-consumption-date"
            :value="recommendedConsumptionDateTimestamp"
            :min-date="minDate"
            :max-date="maxDate"
            @selectedValue="(value) => nutritionData.recommendedConsumptionDate = formatDate(value)"
          />
        </div>
    </div>
  </div>
  </template>
  
  <script setup>
  import { ref, reactive, watch, computed } from "vue";
  import { DatePickerV2, Input } from "@n11/so-ui-components";
  
  const nutritionData = reactive({
    allergenWarning: '',
    storageConditions: '',
    usageRecommendations: '',
    contents: '',
    expirationDate: null,
    recommendedConsumptionDate: null
  });

  // DatePicker için min ve max tarihler
  const minDate = new Date(1900, 0, 1).getTime();
  const maxDate = new Date(2100, 11, 31).getTime();
  
  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  };

  // Tarih string'ini timestamp'e çevir
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    
    // DD/MM/YYYY formatını parse et
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month 0-indexed
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day).getTime();
    }
    
    // Eğer zaten timestamp formatındaysa
    if (typeof dateString === 'number' || !isNaN(Date.parse(dateString))) {
      return new Date(dateString).getTime();
    }
    
    return null;
  };

  // DatePicker için timestamp değerleri
  const expirationDateTimestamp = computed(() => {
    return parseDateString(nutritionData.expirationDate);
  });

  const recommendedConsumptionDateTimestamp = computed(() => {
    return parseDateString(nutritionData.recommendedConsumptionDate);
  });
  
const getNutritionInfo = () => {
  return {
    allergenWarning: nutritionData.allergenWarning,
    storageConditions: nutritionData.storageConditions,
    usageRecommendations: nutritionData.usageRecommendations,
    contents: nutritionData.contents,
    expirationDate: nutritionData.expirationDate,
    recommendedConsumptionDate: nutritionData.recommendedConsumptionDate
  };
};

const loadTemplateData = (data) => {
  try {
    if (!data) return;

    if (data.allergenWarning) {
      nutritionData.allergenWarning = data.allergenWarning;
    }
    if (data.storageConditions) {
      nutritionData.storageConditions = data.storageConditions;
    }
    if (data.usageRecommendations) {
      nutritionData.usageRecommendations = data.usageRecommendations;
    }
    if (data.contents) {
      nutritionData.contents = data.contents;
    }
    if (data.expirationDate) {
      nutritionData.expirationDate = data.expirationDate;
    }
    if (data.recommendedConsumptionDate) {
      nutritionData.recommendedConsumptionDate = data.recommendedConsumptionDate;
    }
  } catch (error) {
    console.error('ProductNutritionInfo template data yüklenirken hata:', error);
  }
};

defineExpose({
  getNutritionInfo,
  loadTemplateData,
});
  </script>
  
  <style lang="scss" scoped>
  @import "@/assets/styles/variables.scss";
  
  .nutrition-info {
    margin-bottom: 24px;
    
    div {
      width: 100%;
    }
  }
  .product-safety-content,
  .box {
  
    .title-area {
      .title {
        font-size: 14px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        margin-bottom: 16px;
      }

      .title-sub {
        font-size: 14px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        margin-bottom: 5px;
      }
  
      .description {
        font-size: 11px;
        line-height: 14px; 
        height: 30px;
      }
    }
    &-form-area {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin: 16px 0px; 
      padding: 24px;
      border-radius: 8px;
      border: 1px solid $colorM10;
      background-color: #F8F7FC;
    }
    
    .last-consumption-date,
    .recommended-consumption-date {
      --input-height: 40px;
      
      :deep(.dp__input__wrapper) {
        height: var(--input-height);
        color: #7382a0;
        padding: 8px 12px 8px 16px;
      }
      
      :deep(.dp__input) {
        height: 100%;
        color: #7382a0;
        font-size: 12px;
        width: 100%;
      }
    }
  }
  .product-safety-content {
    margin-top: 24px;
  
    &-boxes {
      display: flex;
      justify-content: space-around;
      gap: 24px;
  
      .box {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
    }
  }
  .last-consumption-date {
  :deep(.dp__input__wrapper) {
    position: relative;
  }
  :deep(.dp__input__wrapper)::before,
  :deep(.dp__input__wrapper)::after {
    position: absolute;
    right: 12px;
    left: auto;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
  :deep(.dp__input__wrapper:has(.dp__input:not(:placeholder-shown))) {
    padding: 18px 12px 8px 16px; /* label için üst boşluk */
  }
  :deep(.dp__input__wrapper:has(.dp__input:not(:placeholder-shown)))::before {
    content: "Son Tüketim Tarihi";
    position: absolute;
    left: 16px;
    right: auto;
    top: 4px;
    transform: none; /* üst-sola sabitle */
    font-size: 10px;
    color: #7382a0;
  }
}
.recommended-consumption-date {
  :deep(.dp__input__wrapper) {
    position: relative;
  }
  :deep(.dp__input__wrapper)::before,
  :deep(.dp__input__wrapper)::after {
    position: absolute;
    right: 12px;
    left: auto;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
  :deep(.dp__input__wrapper:has(.dp__input:not(:placeholder-shown))) {
    padding: 18px 12px 8px 16px;
  }
  :deep(.dp__input__wrapper:has(.dp__input:not(:placeholder-shown)))::before {
    content: "Tavsiye Edilen Tüketim Tarihi";
    position: absolute;
    left: 16px;
    right: auto;
    top: 4px;
    transform: none;
    font-size: 10px;
    color: #7382a0;
  }


}
  </style>